import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Input, Tooltip, message } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import {
  AlertCircleFilledIcon,
  LayoutGridIcon,
  MousePointerClickIcon,
  PencilIcon,
  TrashIcon
} from './lucideIcons';
import style from './index.scss';
import {
  EVENT_CATEGORY,
  MAX_SPATIAL_FIELDS,
  MAX_ON_CLICK_FIELDS,
  isIdentifier,
  isTimestamp,
  isRequiredField
} from './constants';

/**
 * The configured-field list for one event type.
 *
 *   FIELD                      SPATIAL LAYOUT   ON CLICK
 *   Activity ID  [Identifier]        [x]          [x]
 *   activity_id
 *   Device            [trash]        [x]          [x]
 *   device
 *
 * PLAIN <table>, NOT antd's. The design is three columns with two-line cells, and
 * antd's Table brings a wrapper, its own borders and its own cell padding — each of
 * which would need overriding. Every antd override in the Spatial View cost a round
 * of specificity debugging (a module class on an antd root ties that component's
 * base class and loses on source order), so the cheaper choice here is markup we
 * own outright. antd is still used for the Checkbox, which is a real control.
 *
 * PRESENTATIONAL. It holds no state: the page owns the draft, so Reset is throwing
 * the draft away rather than unwinding anything in here.
 */
/**
 * Whether the rename affordance is offered. FALSE — the design's field row
 * carries a trash and nothing else. See the note beside the label cell.
 */
export const RENAME_ENABLED = false;

/**
 * The cap notice.
 *
 * `message.open` rather than `message.info`: the design's notice is a GREY card
 * led by a solid dark circle-alert, while `info` hard-codes a blue circled "i"
 * on white. Passing the icon and a className is the only way to reach both.
 */
const capNotice = (content) =>
  message.open({
    content,
    icon: <AlertCircleFilledIcon />,
    className: style.capNotice
  });

const FieldConfigTable = ({
  fields,
  category,
  onToggle,
  onDelete,
  onRename,
  maxSpatial,
  maxOnClick,
  /* AC-2: "No seed field can be removed from the type definition."
     The BE enforces it — PUT .../fields on a system type answers "field keys are
     fixed and cannot be added or removed. Only showSpatial/showOnClick may
     change" — so the row's delete is ABSENT here, exactly as the identifier's
     is. The two checkboxes stay live: changing them is the one edit the server
     still accepts on these types. */
  systemType = false,
  // Test-only seam; see RENAME_ENABLED and the note beside the label cell.
  // Same pattern as AlertTabs' `parkedTabKeys`: the parked behaviour keeps its
  // tests rather than rotting, and restoring it is a one-line change here.
  renameEnabled = RENAME_ENABLED
}) => {
  // Which row is being renamed, and the text so far. One at a time — an inline
  // editor per row would leave several half-finished edits with no obvious way to
  // tell which are committed.
  const [editingKey, setEditingKey] = useState(null);
  const [draftLabel, setDraftLabel] = useState('');

  const startRename = (field) => {
    setEditingKey(field.fieldKey);
    setDraftLabel(field.displayLabel || '');
  };

  const commitRename = () => {
    if (!editingKey) return;
    // An empty label means "use the schema's own name": `display_label` is
    // nullable on the BE and null means exactly that. So clearing the box is how
    // an admin undoes a rename without having to remember the original.
    onRename(editingKey, draftLabel.trim() || null);
    setEditingKey(null);
  };
  /* Forced on in both columns and not unticked by the admin, so excluded from
     the caps and disabled in the table — the identifier and the timestamp, in
     both categories (FIN-13594). `isIdentifier` needs the category;
     `isTimestamp` does not, because the two timestamp keys are distinct. */
  const isAlwaysOn = (field) =>
    Boolean(field) &&
    (isIdentifier(field.fieldKey, category) || isTimestamp(field.fieldKey));

  /* THE CAPS COUNT THE ADMIN'S OWN CHOICES, not the required rows (FIN-13594).
     `validateFields` builds `userFields = fields.filterNot(excludedKeys.contains)`
     with `excludedKeys = Set(idKey, dtKey)` and counts THAT against its limits,
     so the screen has to exclude the same two or it is stricter than the API it
     saves to.

     EXCLUDED BY `isRequiredField` — which rows the SERVER excludes from its
     count. Since the transaction timestamp is locked too, this is now the same
     set as `isAlwaysOn`; the server's rule is the one kept here because it is
     the contract the save has to satisfy. */
  const chosen = fields.filter((f) => !isRequiredField(f?.fieldKey, category));
  const spatialCount = chosen.filter((f) => f.showSpatial).length;
  const onClickCount = chosen.filter((f) => f.showOnClick).length;

  // Story §11: "once three are selected, additional unchecked checkboxes should be
  // disabled". Disabled rather than silently ignoring the click — a checkbox that
  // does nothing when clicked reads as broken.
  const deprecatedCount = fields.filter((f) => f.isDeprecated).length;

  const spatialFull = spatialCount >= maxSpatial;
  const onClickFull = maxOnClick != null && onClickCount >= maxOnClick;

  return (
    <div className={style.fieldTableWrap}>
      <table className={style.fieldTable}>
        <thead>
          <tr>
            <th>Field</th>
            {/* The design prefixes each column label with a glyph — `layout-grid`
                for the surface the node draws on, `mouse-pointer-click` for the
                panel that opens on click. Ours were bare text. */}
            <th className={style.checkColSpatial}>
              <span className={style.checkColHead}>
                <LayoutGridIcon />
                Spatial Layout
              </span>
            </th>
            <th className={style.checkColOnClick}>
              <span className={style.checkColHead}>
                <MousePointerClickIcon />
                On Click
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => {
            const identifier = isIdentifier(field.fieldKey, category);
            /* FIN-13594 — the timestamp is locked on in both columns, like the
               identifier. Its DELETE stays available (see below): the identifier
               cannot be removed at all, but a timestamp is an ordinary
               configured field an admin may drop from the type entirely — what
               is locked is only its visibility while it IS configured. */
            const alwaysOn = isAlwaysOn(field);
            /* NO `spatialLocked` / `onClickLocked` any more. They existed to
               disable every unchecked box once a cap was reached; the caps now
               refuse the change and say why instead, so the only thing that
               disables a box is the identifier. `spatialFull` / `onClickFull`
               are read directly in the handlers below. */

            return (
              <tr key={field.fieldKey}>
                <td>
                  <div className={style.fieldName}>
                    {editingKey === field.fieldKey ? (
                      <Input
                        size="small"
                        autoFocus
                        value={draftLabel}
                        className={style.renameInput}
                        aria-label={`Rename ${field.displayLabel}`}
                        placeholder="Use the schema name"
                        onChange={(e) => setDraftLabel(e.target.value)}
                        onBlur={commitRename}
                        onPressEnter={commitRename}
                        // Escape abandons the edit — a rename is cheap to redo
                        // and an accidental commit is not obvious afterwards.
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') setEditingKey(null);
                        }}
                      />
                    ) : (
                      <>
                        {/* Wrapped so it can clip: the design's label is
                            `truncate`, so a long field name ends in an ellipsis
                            instead of wrapping and making one row taller than
                            its neighbours. */}
                        <span className={style.fieldLabel}>
                          {field.displayLabel}
                        </span>
                        {/* NO RENAME PENCIL BY DEFAULT. The design's field row
                            carries a trash and nothing else (checked against it
                            2026-09-02); ours added an edit affordance beside
                            every label, which put two controls where the design
                            has one and made the row read busier than the type
                            list beside it.
                            §9 ("the label is the admin's to change") therefore
                            has no entry point on this screen — the rename
                            behaviour is PARKED, not deleted, pending a decision
                            on whether that requirement is dropped or moves
                            elsewhere. `renameEnabled` keeps it exercised so it
                            cannot rot while parked; flipping RENAME_ENABLED is
                            all that restoring it takes. */}
                        {renameEnabled ? (
                          <button
                            type="button"
                            className={style.headerAction}
                            aria-label={`Rename ${field.displayLabel}`}
                            onClick={() => startRename(field)}>
                            <PencilIcon />
                          </button>
                        ) : null}
                      </>
                    )}
                    {/* PRD AC: a field that has left the schema must be "flagged
                        for administrator attention rather than silently removed".
                        So the row stays, keeps its checkboxes, and says why it can
                        never render — removing it quietly would hide the fact that
                        a configuration the admin made has stopped working.
                        `isDeprecated` is computed by the BE at read time against
                        the live available-fields set. */}
                    {field.isDeprecated ? (
                      <Tooltip title="This field is no longer in the API schema, so it cannot be displayed. Remove it, or restore the field in Custom Fields.">
                        <span className={style.deprecatedBadge}>
                          <WarningOutlined /> Not in schema
                        </span>
                      </Tooltip>
                    ) : null}

                    {isRequiredField(field.fieldKey, category) || systemType ? (
                      identifier ? (
                        <span className={style.identifierBadge}>
                          Identifier
                        </span>
                      ) : null
                    ) : (
                      /* §13/§14: the identifier's delete is ABSENT, not disabled.
                         A greyed control invites the click and then refuses it. */
                      <button
                        type="button"
                        className={style.deleteField}
                        aria-label={`Remove ${field.displayLabel}`}
                        onClick={() => onDelete(field.fieldKey)}>
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                  {/* The technical name is shown alongside the label everywhere in
                      this screen — it is what must match the payload exactly, so
                      hiding it would hide the thing that makes a config correct. */}
                  <div className={style.fieldKey}>{field.fieldKey}</div>
                </td>

                <td className={style.checkColSpatial}>
                  {/* THE IDENTIFIER IS LOCKED ON — checked and faded. §13 says
                      it cannot be removed; the requirement is that it cannot be
                      hidden either, so the control states the fact.
                      adapters.toFields forces the value; this only disables it.

                      THE CAP DOES NOT DISABLE ANYTHING (changed on request).
                      It used to grey every unchecked box once three were
                      ticked, which made a whole column look dead — and on
                      LOGIN_MFA_WITH_OTP and ACCOUNT_PASSWORD_RESET, which
                      already hold three, that was most of the table. The boxes
                      now stay live and a click that would exceed the cap SAYS
                      SO instead, which is also how the design shows it: a
                      notice, not a dead control. */}
                  <Checkbox
                    checked={field.showSpatial}
                    disabled={alwaysOn}
                    aria-label={`${field.displayLabel} on the spatial layout`}
                    onChange={() => {
                      if (!field.showSpatial && spatialFull) {
                        capNotice(
                          `Spatial layout supports up to ${maxSpatial} fields per event type.`
                        );
                        return;
                      }
                      onToggle(field.fieldKey, 'showSpatial');
                    }}
                  />
                </td>

                <td className={style.checkColOnClick}>
                  {/* Locked on for the identifier — no hint; see the spatial
                      column above. */}
                  <Checkbox
                    checked={field.showOnClick}
                    disabled={alwaysOn}
                    aria-label={`${field.displayLabel} on click`}
                    onChange={() => {
                      if (!field.showOnClick && onClickFull) {
                        capNotice(
                          `The on-click panel supports up to ${maxOnClick} fields per event type.`
                        );
                        return;
                      }
                      onToggle(field.fieldKey, 'showOnClick');
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {deprecatedCount > 0 ? (
        <p className={style.deprecatedNote}>
          <WarningOutlined /> {deprecatedCount}{' '}
          {deprecatedCount === 1 ? 'field is' : 'fields are'} no longer in the
          API schema and cannot be displayed. Remove{' '}
          {deprecatedCount === 1 ? 'it' : 'them'}, or restore the{' '}
          {deprecatedCount === 1 ? 'field' : 'fields'} under Custom Fields.
        </p>
      ) : null}

      {/* The ID and the timestamp are pinned on both surfaces, so the limits
          are on the admin's own choices — what the backend counts too. */}
      <p className={style.fieldTableNote}>
        The ID and timestamp are always shown. Up to {maxSpatial} more fields
        can be rendered under a node in the spatial layout
        {maxOnClick == null
          ? '; the on-click panel has no limit.'
          : `; up to ${maxOnClick} more in the on-click panel.`}
      </p>
    </div>
  );
};

FieldConfigTable.propTypes = {
  /** Configured fields, in render order. Shape mirrors `EventTypeFieldEntry`. */
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      fieldKey: PropTypes.string.isRequired,
      displayLabel: PropTypes.string,
      showSpatial: PropTypes.bool,
      showOnClick: PropTypes.bool,
      /** Set by the BE when the key has left the schema — see the badge above. */
      isDeprecated: PropTypes.bool
    })
  ),
  category: PropTypes.oneOf(Object.values(EVENT_CATEGORY)).isRequired,
  /** (fieldKey, 'showSpatial' | 'showOnClick') */
  onToggle: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  /** (fieldKey, label | null) — null restores the schema's own name. */
  onRename: PropTypes.func.isRequired,
  maxSpatial: PropTypes.number,
  /** null means no limit — see MAX_ON_CLICK_FIELDS. */
  maxOnClick: PropTypes.number,
  // Test-only seam; see the destructure above.
  renameEnabled: PropTypes.bool,
  /** True for the immutable system-default type — hides the per-row delete. */
  systemType: PropTypes.bool
};

FieldConfigTable.defaultProps = {
  fields: [],
  maxSpatial: MAX_SPATIAL_FIELDS,
  systemType: false,
  maxOnClick: MAX_ON_CLICK_FIELDS
};

export default FieldConfigTable;
